import { NgOptimizedImage, NgTemplateOutlet } from "@angular/common";
import { Component, computed, DestroyRef, effect, inject, input, output, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { GoogleMapsModule } from "@angular/google-maps";
import { TranslocoPipe } from "@jsverse/transloco";
import { isDefined } from "@recursyve/nice-ts-utils";
import { SvgIconComponent } from "angular-svg-icon";
import { ImageLoadingDirective } from "../../../../../../common/directives/image-loading.directive";
import { DirectusTranslationPipe } from "../../../../../../common/pipes/directus-translation.pipe";
import { GetDirectusImagePipe } from "../../../../../../common/pipes/get-directus-image.pipe";
import { IsDefinedPipe } from "../../../../../../common/pipes/is-defined.pipe";
import { PathFromItemPipe } from "../../../../../../common/pipes/path-from-item.pipe";
import {
    sanitizeDirectusObject,
    sanitizeDirectusRelationArray
} from "../../../../../../common/utils/sanitize-directus-object";
import { ItemsSuppliers } from "../../../../../api/packbo/models/items-suppliers";
import { PackageBadgeComponent } from "../../../../../components/package-badge/package-badge.component";
import { CalculateDistancePipe } from "./pipes/calculate-distance.pipe";
import { GetCategoryPipe } from "./pipes/get-category.pipe";
import { SanitizeSupplierCoordPipe } from "./pipes/sanitize-supplier-coord.pipe";
import { SanitizeSupplierPackagePipe } from "./pipes/sanitize-supplier-package.pipe";
import { SupplierImageDataPipe } from "./pipes/supplier-image-data.pipe";
import { GeocodingService } from "./services/geocoding.service";

@Component({
    selector: "packbo-supplier-card",
    templateUrl: "./supplier-card.template.html",
    imports: [
        NgOptimizedImage,
        NgTemplateOutlet,
        GetDirectusImagePipe,
        SanitizeSupplierPackagePipe,
        SanitizeSupplierCoordPipe,
        PackageBadgeComponent,
        TranslocoPipe,
        SvgIconComponent,
        DirectusTranslationPipe,
        GetCategoryPipe,
        ImageLoadingDirective,
        PathFromItemPipe,
        CalculateDistancePipe,
        SupplierImageDataPipe,
        GoogleMapsModule,
        IsDefinedPipe
    ]
})
export class SupplierCardComponent {
    private readonly geocodingService = inject(GeocodingService);
    private readonly destroyRef = inject(DestroyRef);

    public readonly suppliers = input.required<ItemsSuppliers[]>();
    public readonly postalCode = input<string | null>(null);
    protected readonly allImagesLoaded = output<boolean>();

    public readonly userLocation = signal<google.maps.LatLngLiteral | null>(null);

    public readonly supplierPackage = computed(() =>
        sanitizeDirectusRelationArray(this.suppliers(), "package_ref").map(sanitizeDirectusObject).filter(isDefined)
    );

    constructor() {
        effect(() => {
            const postal = this.postalCode();
            if (postal?.trim()) {
                this.geocodePostalCode(postal);
            } else {
                this.userLocation.set(null);
            }
        });
    }

    public onAllImagesLoaded(imageLoaded: boolean): void {
        this.allImagesLoaded.emit(imageLoaded);
    }

    private geocodePostalCode(postalCode: string): void {
        this.geocodingService
            .geocodePostalCode(postalCode)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((location) => {
                this.userLocation.set(location);
            });
    }
}
